import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import type { ReactNode } from "react";
import {
  SidebarNavItem,
  type SidebarItem,
  type SidebarLinkProps,
} from "./sidebar-nav-item";
import { SidebarOwnerLinks } from "./sidebar-owner-links";

type CoworldSidebarViewProps = {
  readonly items: readonly SidebarItem[];
  readonly ariaLabel: string;
  readonly homeHref: string;
  readonly brandmarkSrc: string;
  readonly links?:
    | readonly { readonly label: string; readonly href: string }[]
    | undefined;
  readonly switcher: ReactNode;
  readonly search: ReactNode;
  readonly account: ReactNode;
  readonly renderLink: (props: SidebarLinkProps) => ReactNode;
};

export function CoworldSidebarView({
  items,
  ariaLabel,
  homeHref,
  brandmarkSrc,
  links,
  switcher,
  search,
  account,
  renderLink,
}: CoworldSidebarViewProps) {
  return (
    <nav aria-label={ariaLabel} {...stylex.props(styles.frame)}>
      <div {...stylex.props(styles.top)}>
        {renderLink({
          href: homeHref,
          "aria-label": "Softmax home",
          ...stylex.props(styles.home),
          children: (
            <img src={brandmarkSrc} alt="" {...stylex.props(styles.brand)} />
          ),
        })}
        <span aria-hidden="true" {...stylex.props(styles.separator)}>
          /
        </span>
        {switcher}
      </div>
      {search}
      {items.map((item) => (
        <SidebarNavItem key={item.key} item={item} renderLink={renderLink} />
      ))}
      {links?.length ? <SidebarOwnerLinks links={links} /> : null}
      <div {...stylex.props(styles.account)}>{account}</div>
    </nav>
  );
}

const styles = stylex.create({
  frame: {
    position: "sticky",
    top: 0,
    display: { default: "none", "@media (min-width: 1024px)": "flex" },
    height: "100dvh",
    width: 248,
    boxSizing: "border-box",
    flexShrink: 0,
    flexDirection: "column",
    gap: 2,
    borderRightWidth: 1,
    borderRightStyle: "solid",
    borderRightColor: colors.border,
    paddingInline: spacing.space16,
    paddingBlock: spacing.space24,
  },
  top: {
    display: "flex",
    height: 64,
    flexShrink: 0,
    alignItems: "center",
    gap: spacing.space8,
    paddingLeft: spacing.space12,
    marginBottom: spacing.space8,
  },
  home: {
    position: "relative",
    display: "block",
    width: 16,
    height: 16,
    flexShrink: 0,
    opacity: { default: 1, ":hover": 0.7, ":active": 0.5 },
    transitionProperty: "opacity",
    transitionDuration: "150ms",
    "::after": {
      content: { default: "none", "@media (pointer: coarse)": '""' },
      position: "absolute",
      inset: -14,
    },
  },
  brand: { width: 16, height: 16, display: "block" },
  separator: {
    flexShrink: 0,
    fontFamily: fonts.mono,
    fontSize: fontSizes.label,
    color: colors.muted,
  },
  account: { marginTop: "auto" },
});
